import {
  AlertTitle,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Button as MuiButton,
} from "@mui/material";
import { type ISelectObject } from "@/types/select";
import {
  type ICommercialSearchFormData,
  type CommercialReceptionBookViewPlan,
} from "../types";
import { SectionPaper } from "@/components/Paper";
import { SectionTitle } from "@/components/Title";
import { Divider } from "@/components/Divider";
import { Label } from "@/components/Label";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { CTextField } from "@/components/TextField/CTextField";
import { CDateRangePicker } from "@/components/DatePicker/CDateRangePicker";
import { getStartAndEndDate } from "@/features/commercialRegistrationReceptionBookFeed/utils/dateTime";
import Tooltip from "@mui/material/Tooltip";
import { CMultiSelect } from "@/components/Select/CMultiSelect";
import { CFeedReceptionReasonSelect } from "@/features/commercialRegistrationReceptionBookFeed/components/CFeedReceptionReasonSelect";
import CachedIcon from "@mui/icons-material/Cached";

interface Props {
  handleSearch: (formData: ICommercialSearchFormData) => void;
  handleOpenSearchHistory: () => void;
  startDate: Date;
  minDate: Date;
  maxDate: Date;
  requesterTypeOptions: ISelectObject[];
  commercialReceptionReasonOptions: ISelectObject[];
  prefectureOptions: ISelectObject[];
  errorMessages: {
    dateStart: string;
    dateEnd: string;
  };
  plan: CommercialReceptionBookViewPlan;
}

export const SearchCondition: React.FC<Props> = (props) => {
  const {
    startDate,
    minDate,
    maxDate,
    requesterTypeOptions,
    commercialReceptionReasonOptions,
    prefectureOptions,
    handleSearch,
    errorMessages,
    plan,
  } = props;

  const [_, endDate] = getStartAndEndDate();

  // Zodスキーマ定義
  const FormDataSchema = z.object({
    companyNumber: z.string(),
    companyName: z.string(),
    companyType: z.array(z.string()),
    receptionDateRange: z.tuple([
      z.date().min(minDate).max(maxDate),
      z.date().min(minDate).max(maxDate),
    ]),
    receptionNumber: z.string(),
    receptionType: z.array(z.string()),
    remarks: z.string(),
    prefectures: z.array(z.string()),
    purchaseStatusPurchased: z.boolean(),
    purchaseStatusUnpurchased: z.boolean(),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type FormData = z.infer<typeof FormDataSchema>;

  // 型を用いてReact-Hook-Formのインスタンスを作る
  const { control, reset, handleSubmit } = useForm<FormData>({
    mode: "all",
    defaultValues: {
      companyNumber: "",
      companyName: "",
      companyType: [],
      receptionDateRange: [startDate, endDate],
      receptionNumber: "",
      receptionType: [],
      remarks: "",
      prefectures: [],
      purchaseStatusPurchased: false,
      purchaseStatusUnpurchased: false,
    },
    resolver: zodResolver(FormDataSchema),
  });

  return (
    <SectionPaper>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <SectionTitle>検索</SectionTitle>
      </Box>
      <Box sx={{ mx: 2 }}>
        <Divider />
      </Box>

      {/* 商業登記情報 */}
      <Box mx={2} mb={1}>
        <Grid container mt={1} rowSpacing={2} columnSpacing={1}>
          <Grid item xs={12} md={12}>
            <Box>
              <Label>検索条件指定</Label>
              <AlertTitle
                sx={{ fontWeight: "bold", color: "red", fontSize: "0.85rem" }}
              >
                ・受付年月日は範囲指定が可能です
              </AlertTitle>
            </Box>
          </Grid>
          <Grid item xs={12} md={12}>
            <Tooltip title={"Coming soon"} placement={"top"} arrow={true}>
              <Box display={"inline-flex"} width={"fit-content"}>
                <Button
                  label="検索履歴"
                  onClick={props.handleOpenSearchHistory}
                  variant={ButtonVariantOption.Outlined}
                  size={SizingWrapperStyle.SMALL}
                  disabled={true}
                  fullWidth={false}
                />
              </Box>
            </Tooltip>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Label>都道府県</Label>
              <CMultiSelect
                control={control}
                name={"prefectures"}
                label="都道府県"
                options={prefectureOptions}
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
          <Grid item xs={false} md={8} />
          <Grid item xs={12} md={4}>
            <Box>
              <Label>会社法人等番号</Label>
              <CTextField
                control={control}
                name="companyNumber"
                placeholder="会社法人等番号"
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Label>法人名</Label>
              <CTextField
                control={control}
                name="companyName"
                placeholder="法人名"
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box>
              <Label>種別</Label>
              {/* 種別は従来どおり1件のみ選択可能（複数選択に対応するのは登記の種類のみ） */}
              <CFeedReceptionReasonSelect
                id="commercial-requester-type"
                control={control}
                name={"companyType"}
                label="種別"
                maxSelect={1}
                options={requesterTypeOptions}
                error={false}
                formHelperText={""}
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: "flex" }}>
              <CDateRangePicker<FormData>
                control={control}
                name={"receptionDateRange"}
                startDateLabel={"受付年月日　開始"}
                endDateLabel={"受付年月日 終了"}
                minDate={minDate}
                maxDate={maxDate}
                defaultCalendarMonth={startDate}
                displayDateLabel={true}
                errorStart={errorMessages.dateStart}
                errorEnd={errorMessages.dateEnd}
                requiredStart={true}
                requiredEnd={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Label>受付番号</Label>
              <CTextField
                control={control}
                name={"receptionNumber"}
                placeholder="受付番号"
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box>
              <Label>登記の種類</Label>
              <CFeedReceptionReasonSelect
                id="commercial-reception-type"
                control={control}
                name={"receptionType"}
                label="登記の種類"
                options={commercialReceptionReasonOptions}
                error={false}
                formHelperText={""}
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
          {plan !== "flat_monthly" && (
            <Grid item xs={12} md={12}>
              <Box>
                <Label>購入状況</Label>
                <FormGroup row>
                  <Controller
                    control={control}
                    name="purchaseStatusPurchased"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(_, checked) => {
                              field.onChange(checked);
                            }}
                          />
                        }
                        label="購入済み"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="purchaseStatusUnpurchased"
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(_, checked) => {
                              field.onChange(checked);
                            }}
                          />
                        }
                        label="未購入"
                      />
                    )}
                  />
                </FormGroup>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={12}>
            <Box>
              <Label>備考</Label>
              <CTextField
                control={control}
                name={"remarks"}
                placeholder="備考"
                size={SizingWrapperStyle.INHERIT}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* 検索 */}
      <Box sx={{ mx: 2 }}>
        <Divider />
      </Box>

      <Box
        sx={{
          my: 3,
          mr: 2,
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <MuiButton
          type={"button"}
          color={"inherit"}
          onClick={() => {
            reset();
          }}
        >
          <CachedIcon />
          クリア
        </MuiButton>
        <Button
          label="この条件で検索"
          onClick={() => {
            void handleSubmit(handleSearch)();
          }}
          variant={ButtonVariantOption.Contained}
          size={SizingWrapperStyle.SMALL}
        />
      </Box>
    </SectionPaper>
  );
};
